/**
 * Stateless builders that turn a compute/recompute response's `result` into the
 * `data`-shaped object each self-service chart component consumes. The DVM
 * layout builder produces these same shapes for its preview tiles + published
 * layout_json; the PUBLISHED dashboard view reuses them here to live-update a
 * target chart when an input control (slider / input box) changes, without a
 * full reload.
 *
 * These are intentionally free of component state — every label / id the shape
 * needs is passed in — so both the builder and the published view can call them.
 */

import { formatNumber } from './number-format.utils';
import { applyChartFont } from '../../utils/chart-font.utils';
import { applyDrillFormulasToTree, drillMeasureKey, stripColumnValue, type DrillFormulaMeta, type RowEvaluatorFactory } from '../../chart-components/unified-table-matrix-chart/drilldown-client-tree.utils';

export type RecomputeChartType =
  | 'WATERFALL'
  | 'DRILL_DOWN_TABLE'
  | 'HORIZONTAL_BAR_CHART'
  | 'KPI'
  | 'INPUT';

interface WaterfallBarLike {
  label: string;
  delta: number;
  bottom: number;
  top: number;
  type?: string;

  colorSign?: string;
}

/** Build the ECharts option for a waterfall bridge from its computed bars. */
function toAxisLabelRotation(value: unknown, fallback: number): number {
  const rotation = Number(value ?? fallback);
  return Number.isFinite(rotation) ? rotation : fallback;
}

/**
 * Attaches each step's configured "Direction" to its bar as `colorSign` — a
 * display-only hint `colorFor` (in buildWaterfallOption) can use instead of
 * the bar's own delta sign. Deliberately does NOT touch delta/bottom/top:
 * those are the backend's own computed bridge values, and "Direction" must
 * only ever choose a color, never recompute the numbers or bridge position
 * it's painted on.
 */
function applyWaterfallStepSigns(
  bars: WaterfallBarLike[],
  payload?: Record<string, unknown>,
): WaterfallBarLike[] {
  if (!Array.isArray(bars) || !bars.length) return [];
  const steps = Array.isArray(payload?.['steps']) ? payload['steps'] as Array<Record<string, unknown>> : [];
  if (!steps.length) return bars;

  const normalize = (value: unknown) => String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
  const stepsByLabel = new Map<string, Record<string, unknown>>();
  steps.forEach((step) => {
    const key = normalize(step['label']);
    if (key) stepsByLabel.set(key, step);
  });

  return bars.map((bar, index) => {
    const matchingStep = stepsByLabel.get(normalize(bar.label)) ?? steps[index];
    const sign = String(matchingStep?.['sign'] ?? 'AUTO').toUpperCase();
    return sign === 'AUTO' ? bar : { ...bar, colorSign: sign };
  });
}

export function buildWaterfallOption(
  bars: WaterfallBarLike[],
  format?: Record<string, unknown>,
): Record<string, unknown> {
  const categories = bars.map((bar) => bar.label);

  const positiveColor = (format?.['positiveColor'] as string) || '#10b981';
  const negativeColor = (format?.['negativeColor'] as string) || '#ef4444';
  const totalColor = (format?.['totalColor'] as string) || '#1f6feb';
  const showValues = format?.['showValues'] !== false;
  const numberFormat = (format?.['numberFormat'] as string) || 'NONE';
  const decimalsRaw = format?.['decimals'];
  const decimals = decimalsRaw !== undefined && decimalsRaw !== null && decimalsRaw !== '' ? Number(decimalsRaw) : undefined;
  // An explicit Number-scale choice (Formatting accordion) overrides the preset's
  // own scaling by mapping onto the equivalent preset; 'none' keeps the preset.
  const numberScale = String(format?.['numberScale'] ?? 'none');
  const scaleToPreset: Record<string, string> = {
    compact: 'COMPACT',
    thousands: 'SCALE_THOUSANDS',
    millions: 'MILLIONS',
    billions: 'BILLIONS',
  };
  const effectiveFormat = scaleToPreset[numberScale] ?? numberFormat;
  const xAxisLabelRotation = toAxisLabelRotation(format?.['xAxisLabelRotation'], 30);
  // Wrap long step labels (e.g. "Marketing Program Discounts") onto a second
  // line instead of overlapping — configurable chars-per-line, 0 (or unset)
  // disables wrapping, showing the full label on one line.
  const xAxisLabelMaxChars = Math.max(0, Number(format?.['xAxisLabelMaxChars'] ?? 14) || 0);
  const wrapLabel = (value: string): string => {
    if (!xAxisLabelMaxChars || value.length <= xAxisLabelMaxChars) return value;
    // Break at the last space at/before the limit (keeps whole words together);
    // no space found (one long word) → hard-break at the limit instead.
    const breakAt = value.lastIndexOf(' ', xAxisLabelMaxChars);
    const splitAt = breakAt > 0 ? breakAt : xAxisLabelMaxChars;
    const firstLine = value.slice(0, splitAt).trim();
    const secondLine = value.slice(breakAt > 0 ? splitAt + 1 : splitAt).trim();
    return `${firstLine}\n${secondLine}`;
  };

  /** Where a bar sits and how tall it is.
   *
   *  Position came from the backend's `bottom`/`top` while height came from
   *  `|delta|` — and those two can DISAGREE, but only when the step's OWN
   *  declared sign contradicts what delta itself computed to. A cost step
   *  tagged NEGATIVE whose value enters the bridge as a POSITIVE amount gets
   *  an ascending pair (e.g. 300 → 507) even though it's meant to descend, so
   *  "Total Cost -207K" climbed 207K instead of dropping to the next total -
   *  THAT mismatch is what needs correcting.
   *
   *  A step whose delta sign already AGREES with its declared sign (Marketing
   *  Discount: tagged NEGATIVE, delta genuinely negative) has bottom/top that
   *  ALREADY correctly span |delta| in the right direction - top>bottom is
   *  true for it too, so checking that alone (without also checking delta's
   *  own sign) wrongly re-derives its base and shifts an already-correct bar
   *  down by its own height a second time.
   *
   *  TOTAL/START bars are absolute (measured from the axis, not relative to a
   *  neighbour) so they're never second-guessed; a bar with no declared sign
   *  at all has nothing to disagree with delta, so it's drawn as sent too. */
  const geometryFor = (bar: WaterfallBarLike): { base: number; height: number } => {
    const base = Math.min(bar.bottom, bar.top);
    const height = Math.abs(bar.delta);
    const type = (bar.type || '').toUpperCase();
    if (type === 'TOTAL' || type === 'START') return { base, height };
    const declaredNegative = bar.colorSign === 'NEGATIVE';
    const declaredPositive = bar.colorSign === 'POSITIVE';
    const deltaNegative = bar.delta < 0;
    if (declaredNegative && !deltaNegative && bar.top > bar.bottom) return { base: bar.bottom - height, height };
    if (declaredPositive && deltaNegative && bar.top < bar.bottom) return { base: bar.bottom, height };
    return { base, height };
  };
  const geometries = bars.map(geometryFor);

  const colorFor = (bar: WaterfallBarLike): string => {
    const type = (bar.type || '').toUpperCase();
    if (type === 'TOTAL' || type === 'START') return totalColor;
    if (bar.colorSign === 'POSITIVE') return positiveColor;
    if (bar.colorSign === 'NEGATIVE') return negativeColor;
    return bar.delta >= 0 ? positiveColor : negativeColor;
  };
  // Label text only — never the bridge math. "Direction" (colorSign) decides
  // whether a "-" prefix shows, exactly like it decides the bar's color; a
  // bar with no explicit direction falls back to its own delta's real sign.
  const displayText = (bar: WaterfallBarLike): string => {
    const isNegative = bar.colorSign === 'NEGATIVE' || (!bar.colorSign && bar.delta < 0);
    return `${isNegative ? '-' : ''}${formatNumber(Math.abs(bar.delta), effectiveFormat, decimals)}`;
  };

  // ── Tooltip (Tooltip accordion): enabled/trigger/pointer + styling. Style
  // props are only set when present in the format so tiles saved before these
  // controls existed keep the ECharts theme defaults.
  const tooltipTrigger = String(format?.['tooltipTrigger'] ?? 'axis');
  const tooltipEnabled = format?.['tooltipEnabled'] !== false && tooltipTrigger !== 'none';
  const axisPointerStyle = String(format?.['tooltipAxisPointer'] ?? 'shadow');
  const tooltipStyle: Record<string, unknown> = {};
  if (format?.['tooltipBackground']) tooltipStyle['backgroundColor'] = format['tooltipBackground'];
  if (format?.['tooltipBorderColor']) tooltipStyle['borderColor'] = format['tooltipBorderColor'];
  const tooltipTextStyle: Record<string, unknown> = {};
  if (format?.['tooltipTextColor']) tooltipTextStyle['color'] = format['tooltipTextColor'];
  const tooltipFontSize = Number(format?.['tooltipFontSize']);
  if (Number.isFinite(tooltipFontSize) && tooltipFontSize > 0) tooltipTextStyle['fontSize'] = tooltipFontSize;
  if (Object.keys(tooltipTextStyle).length) tooltipStyle['textStyle'] = tooltipTextStyle;
  const tooltipBorderRadius = Number(format?.['tooltipBorderRadius']);
  if (Number.isFinite(tooltipBorderRadius)) tooltipStyle['extraCssText'] = `border-radius:${tooltipBorderRadius}px;overflow:hidden;`;

  // ── Legend (Legend accordion): opt-in — only when showLegend is explicitly
  // true, so tiles saved without the key look unchanged. The bridge draws ONE
  // Delta series, so the legend entries come from three data-less marker series
  // (Increase/Decrease/Total) that exist purely for the legend items + swatches.
  const legendPosition = String(format?.['legendPosition'] ?? 'bottom');
  const showLegend = format?.['showLegend'] === true && legendPosition !== 'none';
  const legendNames = ['Increase', 'Decrease', 'Total'];
  const legendMarkerSeries = showLegend
    ? legendNames.map((name, index) => ({
        name,
        type: 'bar',
        stack: 'bridge',
        data: [],
        itemStyle: { color: [positiveColor, negativeColor, totalColor][index] },
      }))
    : [];

  // ── Axes (Axis accordion): optional titles + grid-line toggle.
  const xAxisTitle = String(format?.['xAxisLabel'] ?? '').trim();
  const yAxisTitle = String(format?.['yAxisLabel'] ?? '').trim();
  const showGridLines = format?.['showGridLines'] !== false;

  // ── Advanced accordion: bar width % + animation toggle.
  const barWidthPercent = Number(format?.['barWidthPercent']);
  const barWidth = Number.isFinite(barWidthPercent) && barWidthPercent > 0
    ? `${Math.min(100, Math.max(5, barWidthPercent))}%`
    : undefined;
  const animation = format?.['animation'] !== false;

  // ── Interaction accordion: zoom/pan via ECharts dataZoom. 'category' zooms the
  // step axis (x), 'value' the amount axis (y), 'both' adds both; each zoomed
  // axis gets an inside (wheel/drag) zoom plus an optional slider.
  const enableZoom = format?.['enableZoom'] === true;
  const zoomAxisChoice = String(format?.['zoomAxis'] ?? 'category');
  const showZoomSlider = format?.['showZoomSlider'] !== false;
  const zoomTargets = enableZoom
    ? zoomAxisChoice === 'both' ? ['x', 'y'] : zoomAxisChoice === 'value' ? ['y'] : ['x']
    : [];
  const hasCategoryZoomSlider = showZoomSlider && zoomTargets.includes('x');
  const hasValueZoomSlider = showZoomSlider && zoomTargets.includes('y');
  const dataZoom = zoomTargets.flatMap((axis) => {
    const axisIndex = axis === 'x' ? { xAxisIndex: 0 } : { yAxisIndex: 0 };
    const zooms: Record<string, unknown>[] = [{ type: 'inside', ...axisIndex }];
    if (showZoomSlider) {
      // A bottom legend occupies the bottom edge — lift the x slider above it.
      const legendAtBottom = showLegend && legendPosition !== 'top' && legendPosition !== 'right';
      zooms.push(
        axis === 'x'
          ? { type: 'slider', ...axisIndex, height: 18, bottom: legendAtBottom ? 28 : 6 }
          : { type: 'slider', ...axisIndex, width: 18, right: 6 },
      );
    }
    return zooms;
  });

  return applyChartFont({
    animation,
    tooltip: {
      show: tooltipEnabled,
      trigger: tooltipTrigger === 'item' ? 'item' : 'axis',
      ...(axisPointerStyle !== 'none' ? { axisPointer: { type: axisPointerStyle } } : {}),
      ...tooltipStyle,
      formatter: (params: unknown) => {
        const list = Array.isArray(params) ? params : [params];
        const point = list.find((item) => (item as { seriesName?: string }).seriesName === 'Delta') as
          | { dataIndex: number }
          | undefined;
        if (!point) return '';
        const bar = bars[point.dataIndex];
        if (!bar) return '';
        return `${bar.label}<br/>${displayText(bar)}`;
      },
    },
    ...(showLegend
      ? {
          legend: {
            show: true,
            data: legendNames,
            ...(legendPosition === 'top'
              ? { top: 0 }
              : legendPosition === 'right'
                ? { orient: 'vertical', right: 0, top: 'middle' }
                : { bottom: 0 }),
          },
        }
      : {}),
    ...(dataZoom.length ? { dataZoom } : {}),
    grid: {
      left: yAxisTitle ? 76 : 56,
      right: (showLegend && legendPosition === 'right' ? 120 : 24) + (hasValueZoomSlider ? 32 : 0),
      top: showLegend && legendPosition === 'top' ? 60 : 32,
      bottom:
        (showLegend && legendPosition !== 'top' && legendPosition !== 'right' ? 100 : 72) +
        (xAxisTitle ? 18 : 0) +
        (hasCategoryZoomSlider ? 32 : 0),
      containLabel: false,
    },
    xAxis: {
      type: 'category',
      data: categories,
      ...(xAxisTitle ? { name: xAxisTitle, nameLocation: 'middle', nameGap: 52 } : {}),
      // Pin the axis line (and its category labels) to the BOTTOM of the plot
      // rather than to y=0. ECharts' default (`onZero: true`) draws it through
      // the zero gridline, so as soon as a bridge dips negative the axis cuts
      // straight through the chart and the category names sit mid-plot.
      axisLine: { onZero: false },
      axisLabel: { rotate: xAxisLabelRotation, interval: 0, formatter: wrapLabel },
    },
    yAxis: {
      type: 'value',
      ...(yAxisTitle ? { name: yAxisTitle, nameLocation: 'middle', nameGap: 52 } : {}),
      splitLine: { show: showGridLines },
      // Apply the same number formatting to the axis ticks as the bar labels.
      axisLabel: { formatter: (value: number) => formatNumber(value, effectiveFormat, decimals) },
    },
    series: [
      ...legendMarkerSeries,
      {
        name: 'Base',
        type: 'bar',
        stack: 'bridge',
        stackStrategy: 'all',
        silent: true,
        itemStyle: { color: 'transparent', borderColor: 'transparent', borderWidth: 0 },
        emphasis: {
          disabled: true,
          itemStyle: { color: 'transparent', borderColor: 'transparent', borderWidth: 0 },
        },
        // Keep transparency on every data item as well as the series. ECharts
        // interaction/state patches can replace an item's itemStyle; retaining
        // the colour here prevents the invisible running-balance spacer from
        // becoming a visible bar down to the zero axis.
        data: geometries.map(({ base }) => ({
          value: base,
          itemStyle: { color: 'transparent', borderColor: 'transparent', borderWidth: 0 },
        })),
      },
      {
        name: 'Delta',
        type: 'bar',
        stack: 'bridge',
        stackStrategy: 'all',
        ...(barWidth ? { barWidth } : {}),
        label: {
          show: showValues,
          position: 'top',
          formatter: (params: { dataIndex: number }) => {
            const bar = bars[params.dataIndex];
            return bar ? displayText(bar) : '';
          },
        },
        data: bars.map((bar, index) => ({
          value: geometries[index].height,
          itemStyle: { color: colorFor(bar) },
        })),
      },
    ],
  }, format?.['fontFamily'], format?.['fontSize'], {
    // Waterfall is fully format-driven: the build form's per-axis weight
    // selects write straight into the tile format bag; each falls back to the
    // tile-wide Format-popup "Axis label weight".
    x: format?.['xAxisFontWeight'] || format?.['axisFontWeight'],
    y: format?.['yAxisFontWeight'] || format?.['axisFontWeight'],
  });
}

function toKpiNumberFormat(format?: Record<string, unknown>): string {
  const selected = String(format?.['numberFormat'] ?? 'NONE');
  if (selected === 'THOUSANDS') return 'thousands';
  if (selected === 'PERCENT') return 'percent';
  return 'plain';
}

function toNumberScale(value: unknown): string {
  switch (String(value ?? 'NONE')) {
    case 'COMPACT': return 'compact';
    case 'SCALE_THOUSANDS': return 'thousands';
    case 'MILLIONS': return 'millions';
    case 'BILLIONS': return 'billions';
    default: return 'none';
  }
}

function applySharedFormatFields(data: Record<string, unknown>, format?: Record<string, unknown>): Record<string, unknown> {
  if (!format) return data;
  return {
    ...data,
    title: (format['title'] as string) || data['title'],
    numberFormat: toKpiNumberFormat(format),
    numberScale: toNumberScale(format['numberFormat']),
    decimals: Number(format['decimals'] ?? 1),
    // Free-form text wrapped around the formatted value (e.g. prefix "-",
    // "Δ ", "$" / suffix "%", "#", "&"). KPI-only today, but harmless to
    // carry through for other shared-format consumers (they simply don't
    // render it).
    prefix: (format['prefix'] as string) ?? '',
    suffix: (format['suffix'] as string) ?? '',
    // Hover-tooltip text for the KPI card's info icon (blank hides the icon).
    infoText: (format['infoText'] as string) ?? '',
    infoIconPosition: (format['infoIconPosition'] as string) || 'corner',
    subtitleText: (format['subtitleText'] as string) ?? '',
    subtitleColor: (format['subtitleColor'] as string) ?? '',
    subtitleTopSpacing: format['subtitleTopSpacing'] ?? '',
    subtitleSecondaryText: (format['subtitleSecondaryText'] as string) ?? '',
    subtitleSecondaryColor: (format['subtitleSecondaryColor'] as string) ?? '',
    // Per-tile KPI card layout, from the Format popup's "KPI card" group.
    // Blank/0 = not set, so the THEME's statCard values stay in charge —
    // NumberStats only emits a CSS var for the ones that carry a value.
    ...((format['statAlign'] || format['contentAlignment'] || data['alignment'])
      ? { alignment: String(format['statAlign'] || format['contentAlignment'] || data['alignment']) }
      : {}),
    titleSize: format['statTitleSize'] ?? format['titleFontSize'] ?? '',
    valueSize: format['statValueSize'] ?? format['valueFontSize'] ?? '',
    // Keep the incoming field names too, so old and new NumberStats consumers
    // both receive their expected configuration.
    cardLayout: (format['cardLayout'] as string) || '',
    titleFontSize: format['titleFontSize'] ?? format['statTitleSize'] ?? '',
    valueFontSize: format['valueFontSize'] ?? format['statValueSize'] ?? '',
    // Leading icon, sub-heading caption and trend chip.
    icon: (format['icon'] as string) || '',
    iconColor: (format['iconColor'] as string) || '',
    iconSize: format['iconSize'] ?? '',
    subText: (format['subText'] as string) || '',
    deltaText: (format['deltaText'] as string) || '',
    deltaDirection: (format['deltaDirection'] as string) || 'up',
    deltaColor: (format['deltaColor'] as string) || '',
  };
}

/** Union of measure keys across every node's `measures`, first-seen order. */
function collectMeasureKeys(nodes: Array<Record<string, unknown>>): string[] {
  const keys: string[] = [];
  const seen = new Set<string>();
  const walk = (list: Array<Record<string, unknown>>) => {
    (list || []).forEach((node) => {
      const measures = (node['measures'] as Record<string, unknown>) || {};
      Object.keys(measures).forEach((key) => {
        if (!seen.has(key)) {
          seen.add(key);
          keys.push(key);
        }
      });
      if (Array.isArray(node['children'])) walk(node['children'] as Array<Record<string, unknown>>);
    });
  };
  walk(nodes);
  return keys;
}

/** Deep-copy the tree, lifting each node's `measures` entries to flat top-level
 *  keys so the drill-down table can read them with a plain per-column prop. */
function flattenNodeMeasures(nodes: Array<Record<string, unknown>>, startExpanded = false): Array<Record<string, unknown>> {
  return (nodes || []).map((node) => {
    const measures = (node['measures'] as Record<string, unknown>) || {};
    const rawChildren = Array.isArray(node['children']) ? (node['children'] as Array<Record<string, unknown>>) : [];
    const children = flattenNodeMeasures(rawChildren, startExpanded);
    return {
      ...node,
      ...measures,
      children,
      // Level 1 by default: nodes start COLLAPSED so the table opens showing
      // only the highest level and users expand to drill down. The Format
      // toggle `drillStartExpanded` opts back into every level expanded. An
      // explicit `expanded` flag on the node is respected either way.
      expanded: (node['expanded'] as boolean | undefined) ?? (startExpanded && children.length > 0),
    };
  });
}

/** How an EDITABLE drill-down level / measure is edited. Absent `editor` = a
 *  plain input. `editor: 'dropdown'` renders a select whose options are the
 *  CUSTOM values in `dropdownOptions` (which need not exist in the data at
 *  all) plus — with `dropdownFromData` — the distinct values the column /
 *  level already holds in the table. The builder form keeps the options as
 *  the comma-separated text the user typed; payload / capabilities carry the
 *  parsed array — `parseDropdownOptions` accepts both. */
export interface DrillDownDropdownEditor {
  editor?: 'dropdown' | string;
  dropdownOptions?: string[] | string;
  dropdownFromData?: boolean;
}

/** Normalized dropdown editor as lib-drill-down reads it off a header. */
export interface DrillDownDropdownSpec {
  options: string[];
  fromData: boolean;
}

/** Comma / newline separated text OR an array → trimmed, de-duplicated,
 *  blank-free option list. */
export function parseDropdownOptions(raw: unknown): string[] {
  const parts = Array.isArray(raw) ? raw.map((v) => String(v ?? '')) : String(raw ?? '').split(/[\n,]/);
  const seen = new Set<string>();
  const out: string[] = [];
  parts.forEach((part) => {
    const value = part.trim();
    if (!value || seen.has(value)) return;
    seen.add(value);
    out.push(value);
  });
  return out;
}

/** The dropdown spec for a level / measure config, or null when it edits as
 *  a plain input (or its dropdown has neither custom values nor data values —
 *  an empty select would be unusable, so it falls back to the input). */
function dropdownSpecFor(config: DrillDownDropdownEditor | null | undefined): DrillDownDropdownSpec | null {
  if (config?.editor !== 'dropdown') return null;
  const options = parseDropdownOptions(config.dropdownOptions);
  const fromData = config.dropdownFromData === true;
  return options.length || fromData ? { options, fromData } : null;
}

/** The builder's dimension / measure config that drives the drill-down's
 *  EDITABLE cells. Same shape the compute payload's `dimensions[]` /
 *  `measures[]` and the tile's `capabilities.dimensions/measures` carry, so a
 *  published widget can pass either straight through. */
export interface DrillDownEditConfig {
  /** Hierarchy levels, outermost first. `editable` → that level's node labels
   *  render as text inputs (a rename); with `editor: 'dropdown'` they render
   *  as a select over `dropdownOptions` (custom values) and/or the labels
   *  already in the table (`dropdownFromData`). */
  dimensions?: Array<{ field?: string; label?: string; editable?: boolean; dataset?: string } & DrillDownDropdownEditor>;
  /** Value columns. `editable` → that measure's cells render as inputs;
   *  `editableLevels: [1, 2, …]` (1-based dimension levels) picks WHICH rows
   *  — absent, a text measure edits on every level and a number measure on
   *  leaf rows only (parents + grand total re-sum client-side); `editor:
   *  'dropdown'` swaps the input for a select (see DrillDownDropdownEditor). */
  measures?: Array<{ field?: string; label?: string; editable?: boolean; editableLevels?: number[]; dataset?: string; kind?: string; pivot?: boolean; hidden?: boolean; yearScope?: string; yearField?: string; showYearColumn?: boolean; yearHelper?: boolean } & DrillDownDropdownEditor>;
  /** Merged drill-down + pivot: formula columns evaluated per node on its
   *  aggregated measures. On a BACKEND tree they are evaluated here (needs
   *  `formulaEvaluator`); a CLIENT-built tree (drilldown-client-tree.utils)
   *  already carries them and only gets the metadata lib-drill-down needs to
   *  re-evaluate after an edit. */
  computedColumns?: Array<{ name: string; expression: string }>;
  formulaEvaluator?: RowEvaluatorFactory;
}

/** Formula metadata lib-drill-down re-evaluates with after an edit. A client-
 *  built tree names its base keys exactly (envelope `drillMeta`); a backend
 *  tree's base keys are its response measure keys matched loosely to the
 *  configured measures. Raw field names + labels are aliases of the base key,
 *  so `[Net Sales]` and `net_sales` both resolve in an expression. */
/** Pivot / formula metadata a CLIENT-built tree carries on its envelope. */
type DrillTreeMeta = {
  baseMeasureKeys?: string[];
  columnValues?: string[];
  textMeasureKeys?: string[];
  unsplitKeys?: string[];
  computed?: Array<{ name: string; expression: string; split?: boolean }>;
  /** Which label spans the band above the header: the measure (default) or
   *  the pivot column value (Figma "Mining | Marine | Rail" layout). */
  headerGroupBy?: 'measure' | 'column';
  /** Row-total block column value ("Total") + per-measure header renames
   *  inside it (base key → text, e.g. Sales → "Revenue"). */
  rowTotalValue?: string;
  rowTotalHeaders?: Record<string, string>;
  /** Year-scoped measures (measure `yearScope: 'latest'`): base key → the
   *  single year the values cover, surfaced as header info text. */
  yearScopes?: Record<string, number>;
};
function drillFormulaMetaFrom(
  drillMeta: DrillTreeMeta | undefined,
  responseKeys: string[],
  measures: Array<{ field?: string; label?: string }>,
  computedColumns: Array<{ name: string; expression: string }>,
): DrillFormulaMeta {
  const aliases: Record<string, string> = {};
  const bases = drillMeta?.baseMeasureKeys
    ? [...drillMeta.baseMeasureKeys]
    : measures.map((m) => responseKeys.find((k) => normalizeMeasureKey(k) === normalizeMeasureKey(m.label))
        ?? responseKeys.find((k) => normalizeMeasureKey(k) === normalizeMeasureKey(m.field))
        ?? drillMeasureKey({ field: String(m.field ?? ''), label: m.label }));
  measures.forEach((m, i) => {
    const base = bases[i];
    if (!base) return;
    if (m.field && m.field !== base) aliases[String(m.field)] = base;
    if (m.label && m.label !== base) aliases[String(m.label)] = base;
  });
  return {
    computedColumns: (drillMeta?.computed ?? computedColumns).map((c) => ({ ...c })),
    baseMeasureKeys: bases,
    columnValues: [...(drillMeta?.columnValues ?? [])],
    aliases,
    ...(drillMeta?.unsplitKeys ? { unsplitKeys: [...drillMeta.unsplitKeys] } : {}),
  };
}

/** Case/punctuation-insensitive key so a configured measure label/field can be
 *  matched against the response's own display key ("DN Price" ↔ "dn_price"). */
function normalizeMeasureKey(value: unknown): string {
  return String(value ?? '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** Build the DrillDownComponent `data` input from a compute response.
 *
 *  Columns are derived from the RESPONSE (its actual depth + measure keys),
 *  never from the configured labels — those can differ from what the backend
 *  echoes. The optional `dimensions` / `measures` config only ADDS the
 *  editable-cell markers lib-drill-down reads:
 *  - tree header `editableLevels: [1, 2, …]` (1-based level index of every
 *    dimension flagged Editable) + top-level `levelFields` (level → dataset
 *    field, so a label rename reports the REAL field);
 *  - `editable: true` on the header of every measure flagged Editable (matched
 *    loosely against the response key by label OR field);
 *  - `recalcTotalsOnEdit` (Format popup's `drillEditRecalcTotals`, default on).
 *  Without that config (older callers / tiles) the output is byte-identical to
 *  before — none of the editing keys are emitted. */
export function buildDrillDownWidgetData(
  response: Record<string, unknown> | null,
  opts: { widgetId: string; fallbackHeaderName?: string; format?: Record<string, unknown> } & DrillDownEditConfig,
): Record<string, unknown> {
  const tree = (response?.['data'] ?? response?.['Data'] ?? []) as Array<Record<string, unknown>>;
  const dimensions = Array.isArray(opts.dimensions) ? opts.dimensions : [];
  const measures = Array.isArray(opts.measures) ? opts.measures : [];
  // Merged drill-down + pivot: a CLIENT-built tree (drilldown-client-tree.utils)
  // carries its pivot/formula metadata on the envelope; a backend tree has
  // none, so formula columns are evaluated here on the response's own keys.
  const drillMeta = response?.['drillMeta'] as DrillTreeMeta | undefined;
  const columnValues = drillMeta?.columnValues ?? [];
  // Column-value-major header ("Mining" spanning Sales | Price % | Reason):
  // the tree builder already ordered the keys per column value; here the band
  // label and the sub-header simply swap roles.
  const columnMajorHeader = drillMeta?.headerGroupBy === 'column';
  const computedColumns = (opts.computedColumns ?? []).filter((c) => c?.name && c?.expression);
  const formulaMeta: DrillFormulaMeta | null = computedColumns.length
    ? drillFormulaMetaFrom(drillMeta, collectMeasureKeys(tree), measures, computedColumns)
    : null;
  if (formulaMeta && !drillMeta && opts.formulaEvaluator) {
    applyDrillFormulasToTree(tree, (response?.['total'] ?? response?.['Total']) as Record<string, unknown> | null, formulaMeta, opts.formulaEvaluator);
  }
  // Level 1 by default (`drillStartExpanded` opts back into all-expanded).
  const flattenedTree = flattenNodeMeasures(tree, opts.format?.['drillStartExpanded'] === true);
  // Year-scoped measures ("Latest year only"): the year column (fiscal_year)
  // is plumbing that rides along so the tree can keep the newest year's rows.
  // It never gets a column of its own unless the measure opted in with
  // `showYearColumn` — so a helper measure, a backend tree echoing the field
  // or a stale saved tree can't surface a "Fiscal Year" column. Matched on
  // the normalized base key, so "fiscal_year", "Fiscal Year" and a pivoted
  // "Mining fiscal_year" all drop.
  const hiddenYearKeys = new Set<string>();
  const shownYearKeys = new Set<string>();
  measures
    .filter((measure) => measure?.showYearColumn === true && String(measure.yearField ?? '').trim())
    .forEach((measure) => shownYearKeys.add(normalizeMeasureKey(measure.yearField)));
  measures
    .filter((measure) => measure?.yearScope === 'latest' && measure.yearHelper !== true && measure.showYearColumn !== true && String(measure.yearField ?? '').trim())
    .forEach((measure) => { const key = normalizeMeasureKey(measure.yearField); if (!shownYearKeys.has(key)) hiddenYearKeys.add(key); });
  const measureKeys = collectMeasureKeys(tree).filter((key) =>
    !hiddenYearKeys.size || !hiddenYearKeys.has(normalizeMeasureKey(stripColumnValue(key, columnValues))));

  // Levels whose dimension is flagged Editable; level numbers are 1-based
  // (dimension index + 1, matching the response nodes' `level`).
  const editableLevels = dimensions
    .map((dimension, index) => (dimension?.editable === true ? index + 1 : null))
    .filter((level): level is number => level !== null);
  const levelFields: Record<number, string> = {};
  dimensions.forEach((dimension, index) => {
    if (dimension?.field) levelFields[index + 1] = String(dimension.field);
  });
  // Editable levels whose rename is a DROPDOWN (custom values and/or the
  // labels already in the table) rather than free text: level → editor spec.
  const levelEditors: Record<number, DrillDownDropdownSpec> = {};
  dimensions.forEach((dimension, index) => {
    const spec = dimension?.editable === true ? dropdownSpecFor(dimension) : null;
    if (spec) levelEditors[index + 1] = spec;
  });

  const treeHeader = {
    name: (tree[0]?.['levelLabel'] as string) || opts.fallbackHeaderName || 'Name',
    prop: 'label',
    expandabile: true,
    indentByLevel: true,
    width: 260,
    ...(editableLevels.length ? { editableLevels } : {}),
    ...(Object.keys(levelEditors).length ? { levelEditors } : {}),
  };

  const editableMeasureKeys = new Set<string>();
  // Normalized label/field → the measure's dropdown editor (when it has one),
  // so the header can carry the select's option list.
  const measureEditors = new Map<string, DrillDownDropdownSpec>();
  // Normalized label/field → the 1-based levels the measure edits on (the
  // builder's "Editable on levels" pick); absent = lib-drill-down's default
  // (text: every level, number: leaf rows only).
  const measureLevels = new Map<string, number[]>();
  measures.filter((measure) => measure?.editable === true).forEach((measure) => {
    const spec = dropdownSpecFor(measure);
    const levels = Array.isArray(measure.editableLevels)
      ? Array.from(new Set(measure.editableLevels.map(Number).filter((level) => Number.isInteger(level) && level >= 1))).sort((a, b) => a - b)
      : [];
    [measure.label, measure.field].filter(Boolean).forEach((name) => {
      const key = normalizeMeasureKey(name);
      editableMeasureKeys.add(key);
      if (spec) measureEditors.set(key, spec);
      if (levels.length) measureLevels.set(key, levels);
    });
  });
  // Pivoted keys ("2024 Net Sales") match on their base measure; formula
  // columns are derived values and never editable.
  const computedNames = new Set(computedColumns.map((c) => normalizeMeasureKey(c.name)));
  // TEXT attribute measures render/edit as text (never number-formatted or
  // summed): the client tree names their keys exactly; a backend tree's are
  // matched loosely against measures configured with kind 'text'.
  const textKeys = new Set(drillMeta?.textMeasureKeys ?? []);
  const textBases = new Set<string>();
  measures.filter((measure) => measure?.kind === 'text').forEach((measure) => {
    if (measure.label) textBases.add(normalizeMeasureKey(measure.label));
    if (measure.field) textBases.add(normalizeMeasureKey(measure.field));
  });
  const measureHeaders = measureKeys.map((key) => {
    const rawBase = stripColumnValue(key, columnValues);
    const base = normalizeMeasureKey(rawBase);
    const isText = textKeys.has(key) || textBases.has(base);
    // Pivoted key "2024 Net Sales" → sub-header "2024" under a "Net Sales"
    // group band (lib-drill-down renders `group` as a spanning top header
    // row); `fullName` keeps the unambiguous label for exports. With the
    // column-major layout the roles swap: "Net Sales" under a "2024" band.
    const pivoted = rawBase !== key;
    const columnValue = pivoted ? key.slice(0, key.length - rawBase.length - 1) : '';
    // Row-total block header rename ("Revenue" over the Sales summed across
    // the verticals): display only — prop / fullName keep the real key.
    const isRowTotal = pivoted && !!drillMeta?.rowTotalValue && columnValue === drillMeta.rowTotalValue;
    const rowTotalHeader = isRowTotal ? drillMeta?.rowTotalHeaders?.[rawBase] : undefined;
    const editable = editableMeasureKeys.has(base) && !computedNames.has(base);
    const editor = editable ? measureEditors.get(base) : undefined;
    const levels = editable ? measureLevels.get(base) : undefined;
    // Year-scoped measure ("Latest year only"): the year joins the measure's
    // display label — "Sales (2026)" / "Revenue (2026)" — wherever the base
    // name shows (sub-header in the column-major layout, band in the
    // measure-major one). `yearLabel` rides on the header so the render-time
    // relayout (applyDrillHeaderGrouping) re-applies it after swapping.
    const scopedYear = drillMeta?.yearScopes?.[rawBase];
    const withYear = (label: string): string => (scopedYear ? `${label} (${scopedYear})` : label);
    return {
      name: pivoted ? (columnMajorHeader ? withYear(rowTotalHeader || rawBase) : columnValue) : withYear(key),
      prop: key,
      ...(pivoted ? { group: columnMajorHeader ? columnValue : withYear(rawBase), fullName: key } : {}),
      ...(scopedYear ? { yearLabel: scopedYear } : {}),
      // Kept on the header so the render-time relayout (applyDrillHeaderGrouping)
      // restores the rename after swapping the band and the sub-header.
      ...(rowTotalHeader ? { rowTotalHeader } : {}),
      // Marks the row-total block's columns so the relayout can keep a LEFT
      // block ahead of the leading unsplit columns (rowTotalPosition 'left').
      ...(isRowTotal ? { rowTotal: true as const } : {}),
      type: isText ? 'text' : 'number',
      width: 130,
      ...(editable ? { editable: true as const } : {}),
      // Which tree levels this measure's cells edit on (lib-drill-down's
      // isRowEditable); absent → its text/number default.
      ...(levels?.length ? { editableLevels: levels } : {}),
      // Dropdown editor: lib-drill-down renders a <select> over these options
      // (+ the column's own distinct values when `optionsFromData`).
      ...(editor ? { editor: 'dropdown' as const, options: editor.options, optionsFromData: editor.fromData } : {}),
    };
  });
  const hasEditing = editableLevels.length > 0 || measureHeaders.some((header) => header.editable === true);

  const decimalsRaw = opts.format?.['decimals'];
  const decimals = decimalsRaw !== undefined && decimalsRaw !== null && decimalsRaw !== '' ? Number(decimalsRaw) : undefined;
  return {
    staticData: { data: flattenedTree, total: response?.['total'] ?? response?.['Total'] ?? null },
    nameProp: 'label',
    widgetId: opts.widgetId,
    columnMode: 'force',
    tableWidth: '100%',
    headers: [treeHeader, ...measureHeaders],
    // Level → dataset field, so an editable-label rename reports the field.
    ...(Object.keys(levelFields).length ? { levelFields } : {}),
    // Round-off precision for measure cells + the Total row (Format popup's
    // `decimals`); DrillDownComponent falls back to whole numbers when absent.
    ...(decimals !== undefined && Number.isFinite(decimals) ? { decimals } : {}),
    // Editable measures: whether an edit re-sums parents + the grand total
    // client-side (default on; `!== false` so tiles saved before this control
    // existed keep the recalculating behaviour). Only emitted when something is
    // actually editable, so non-editing output stays unchanged.
    ...(hasEditing ? { recalcTotalsOnEdit: opts.format?.['drillEditRecalcTotals'] !== false } : {}),
    // Formula columns: definitions + base keys so lib-drill-down re-evaluates
    // them after a cell edit re-sums the parents.
    ...(formulaMeta ? { drillFormulas: formulaMeta } : {}),
  };
}

/** Fold a FRESH drill-down build (new compute response) into the widget data a
 *  host already renders, keeping everything the builder baked onto it at
 *  publish time — visual rules, export/print toggles, year filter, title,
 *  widgetId, recalc setting — while the fresh tree + headers replace the data-
 *  bearing keys. Used by the published dashboard's recompute path: swapping
 *  the RAW compute entry in (as it used to) gave lib-drill-down no
 *  `staticData`/`headers` at all, so the table neither re-rendered nor kept
 *  its editable cells after a filter/input-driven recompute.
 *
 *  `inheritEditable` (when the fresh build had no dimension/measure config):
 *  the editable markers are copied from the existing headers by prop, so a
 *  legacy widget whose payload lacks them still keeps its editing. */
export function mergeDrillDownWidgetData(
  existing: Record<string, unknown> | null | undefined,
  fresh: Record<string, unknown>,
  opts: { inheritEditable?: boolean } = {},
): Record<string, unknown> {
  if (!existing) return fresh;
  const previousHeaders = (Array.isArray(existing['headers']) ? existing['headers'] : []) as Array<Record<string, unknown>>;
  const freshHeaders = (Array.isArray(fresh['headers']) ? fresh['headers'] : []) as Array<Record<string, unknown>>;
  const headers = opts.inheritEditable
    ? freshHeaders.map((header) => {
        const previous = previousHeaders.find((candidate) => candidate?.['prop'] === header?.['prop']);
        if (!previous) return header;
        return {
          ...header,
          ...(previous['editable'] === true ? { editable: true } : {}),
          ...(Array.isArray(previous['editableLevels']) ? { editableLevels: previous['editableLevels'] } : {}),
          // Dropdown editors ride along with the editable markers they belong to.
          ...(previous['editor'] === 'dropdown'
            ? { editor: 'dropdown', options: previous['options'] ?? [], optionsFromData: previous['optionsFromData'] === true }
            : {}),
          ...(previous['levelEditors'] && typeof previous['levelEditors'] === 'object' ? { levelEditors: previous['levelEditors'] } : {}),
        };
      })
    : freshHeaders;
  return {
    ...existing,
    ...fresh,
    headers,
    widgetId: existing['widgetId'] ?? fresh['widgetId'],
    // The tile's own recalc preference (Format popup) outranks the default the
    // fresh build derived without access to the tile's format.
    ...(existing['recalcTotalsOnEdit'] !== undefined ? { recalcTotalsOnEdit: existing['recalcTotalsOnEdit'] } : {}),
    // Level → field map only comes from config; keep the baked one otherwise.
    ...(!fresh['levelFields'] && existing['levelFields'] ? { levelFields: existing['levelFields'] } : {}),
  };
}

/** Pull the `horizontalBar` array out of either envelope shape. */
function extractHorizontalBarRows(source: unknown): Array<Record<string, unknown>> | null {
  const container = Array.isArray(source) ? (source[0] as Record<string, unknown>) : (source as Record<string, unknown>);
  const dataArr = container?.['data'] as Array<Record<string, unknown>> | undefined;
  const bar = dataArr?.[0]?.['horizontalBar'];
  return Array.isArray(bar) ? (bar as Array<Record<string, unknown>>) : null;
}

/** True when a BAR tile's value comes from a backend formula rather than a
 *  dataset column — an ordinary bar renders client-side and must be left alone. */
export function barPayloadHasValueFormula(payload: Record<string, unknown> | undefined): boolean {
  const value = (payload?.['value'] as Record<string, unknown> | undefined) ?? {};
  if (String(value['formula'] ?? '').trim()) return true;
  const groupFormulas = value['groupFormulas'] as Record<string, unknown> | undefined;
  return !!groupFormulas && Object.values(groupFormulas).some((formula) => String(formula ?? '').trim());
}

/** Rows for a BAR chart computed server-side: `data[0].bar`, optionally wrapped
 *  in `responseList`. Mirrors extractHorizontalBarRows, keyed `bar`. */
export function extractBarComputeRows(source: unknown): Array<Record<string, unknown>> | null {
  const container = Array.isArray(source) ? (source[0] as Record<string, unknown>) : (source as Record<string, unknown>);
  const dataArr = container?.['data'] as Array<Record<string, unknown>> | undefined;
  const bar = dataArr?.[0]?.['bar'];
  return Array.isArray(bar) ? (bar as Array<Record<string, unknown>>) : null;
}

/** Build the bar chart's `data` input from a compute response. The backend
 *  returns pre-aggregated rows keyed `{ <groupBy>, value }`, so `category` and
 *  `measures` are remapped onto those keys; other capabilities pass through. */
export function buildBarComputeWidgetData(
  response: Record<string, unknown> | null,
  opts: { widgetId: string; title?: string; capabilities?: Record<string, unknown>; payload?: Record<string, unknown>; format?: Record<string, unknown> },
): Record<string, unknown> {
  const rows =
    extractBarComputeRows(response?.['responseList']) ??
    extractBarComputeRows(response) ??
    [];

  const payload = opts.payload ?? {};
  const groupBy = (payload['groupBy'] as Record<string, unknown> | undefined) ?? {};
  const valueBlock = (payload['value'] as Record<string, unknown> | undefined) ?? {};
  const caps = opts.capabilities ?? {};
  const capsMeasures = Array.isArray(caps['measures']) ? (caps['measures'] as Array<Record<string, unknown>>) : [];
  const firstRow = rows[0] ?? {};
  const rowKeys = Object.keys(firstRow);

  // Key must EXIST on the rows: a missing one silently buckets every row under
  // '' and draws a single bar holding their sum. Backend renames it `department`.
  // Plain (non-formula) bar payloads carry no groupBy/value blocks — fall back
  // to the tile's capabilities for the configured column + labels.
  const configuredCategory = String(groupBy['field'] ?? '') || String(caps['category'] ?? '');
  const categoryKey =
    (configuredCategory && rowKeys.includes(configuredCategory) ? configuredCategory : '')
    || (rowKeys.includes('department') ? 'department' : '')
    || rowKeys.find((key) => key !== 'value' && typeof firstRow[key] !== 'number')
    || rowKeys.find((key) => key !== 'value')
    || configuredCategory
    || 'department';

  const valueKey =
    (rowKeys.includes('value') ? 'value' : '')
    || rowKeys.find((key) => key !== categoryKey && typeof firstRow[key] === 'number')
    || 'value';

  const valueLabel = String(valueBlock['label'] ?? '') || String(capsMeasures[0]?.['label'] ?? '') || 'Value';

  // SPLIT / LEGEND column. The backend used to answer with a flat
  // `{ department, value }` row per category — one bar each, genuinely no
  // series — so this was blanked unconditionally. It now returns one row per
  // (category, split) pair, e.g.
  //   { department: 2026, confidence_tier: 'High (>80%)', value: 4494000 }
  // and dropping `series` collapsed those back into a single summed bar: a
  // Stacked Bar rendered as a plain bar with no legend. Keep the configured
  // split whenever the rows actually carry that column; if they don't, fall
  // back to any non-category, non-numeric key (the backend renaming the split
  // the way it renames the group-by to `department`). Still undefined when the
  // rows are the old flat shape, so that contract is unchanged.
  const configuredSeries = String(caps['series'] ?? '');
  const seriesKey =
    (configuredSeries && rowKeys.includes(configuredSeries) ? configuredSeries : '')
    || rowKeys.find((key) => key !== categoryKey && key !== valueKey && typeof firstRow[key] !== 'number')
    || undefined;

  const capabilities = {
    ...(opts.capabilities ?? {}),
    category: categoryKey,
    categoryLabel: String(groupBy['label'] ?? '') || String(caps['categoryLabel'] ?? '') || configuredCategory || categoryKey,
    measures: [{ field: valueKey, label: valueLabel }],
    // Cross-filter publishes a column other tiles filter their RAW rows by, so
    // it keeps the real column, not the remapped `category`.
    ...(configuredCategory && configuredCategory !== categoryKey
      ? { crossFilterKey: (opts.capabilities?.['crossFilterKey'] as string | undefined) || configuredCategory }
      : {}),
    // Resolved above — present only when the rows really carry a split column.
    series: seriesKey,
    // Meaningless on pre-aggregated rows (the backend already applied them).
    computedColumns: undefined,
  };

  return {
    widgetId: opts.widgetId,
    title: opts.title ?? '',
    capabilities,
    staticData: rows,
    height: '100%',
  };
}

/** Rows for a LINE_AREA chart computed server-side: `data[0].lineArea`,
 *  optionally wrapped in `responseList`. Mirrors extractBarComputeRows. */
export function extractLineAreaComputeRows(source: unknown): Array<Record<string, unknown>> | null {
  const container = Array.isArray(source) ? (source[0] as Record<string, unknown>) : (source as Record<string, unknown>);
  const dataArr = container?.['data'] as Array<Record<string, unknown>> | undefined;
  const rows = dataArr?.[0]?.['lineArea'];
  return Array.isArray(rows) ? (rows as Array<Record<string, unknown>>) : null;
}

/** Flat replacement rows for a LINE_AREA chart (the multi-measure `columns`
 *  contract: `data` is a plain array of row objects keyed by each column's
 *  displayName). Only consulted AFTER the `data[0].lineArea` envelope check —
 *  an envelope response's `data[0]` is a wrapper object, not a row. */
export function extractLineAreaFlatRows(source: unknown): Array<Record<string, unknown>> | null {
  const container = Array.isArray(source) ? (source[0] as Record<string, unknown>) : (source as Record<string, unknown>);
  const data = container?.['data'];
  const rows = Array.isArray(data) ? data : (data as { data?: unknown } | undefined)?.data;
  if (!Array.isArray(rows) || !rows.length) return null;
  const first = rows[0];
  if (first == null || typeof first !== 'object' || Array.isArray(first)) return null;
  return rows as Array<Record<string, unknown>>;
}

/** True when compute-response rows can express the configured line-area
 *  capabilities: either some configured measure resolves onto a row key
 *  (the multi-measure flat contract, matched by displayName/label or raw
 *  field), or the config only ever draws ONE series (single measure, no
 *  series split, no secondary X) — which the legacy `{department, value}`
 *  envelope can express. A multi-line config fed single-series rows must NOT
 *  apply them, or the chart collapses to one line. */
export function lineAreaRowsFitCapabilities(
  rows: Array<Record<string, unknown>> | null | undefined,
  capabilities: Record<string, unknown> | null | undefined,
): boolean {
  if (!Array.isArray(rows) || !rows.length) return false;
  const measures = Array.isArray(capabilities?.['measures']) ? (capabilities?.['measures'] as Array<Record<string, unknown>>) : [];
  const multiSeries = measures.length > 1 || !!capabilities?.['series'] || !!capabilities?.['secondaryXField'];
  if (!multiSeries) return true;
  const rowKeys = Object.keys(rows[0] ?? {});
  return measures.some((measure) =>
    rowKeys.includes(String(measure['label'] ?? '')) || rowKeys.includes(String(measure['field'] ?? '')));
}

/** Build the line-area chart's `data` input from a compute response. Two row
 *  shapes are handled:
 *  - the multi-measure flat contract — rows keyed by each `columns[]` entry's
 *    displayName: EVERY configured measure whose displayName/label (or raw
 *    field) is a row key renders as its own series (the multi-line fix);
 *  - the legacy single-series envelope (`{ <groupBy>|department, value }`) —
 *    remapped onto the actual row keys as one measure, as before. */
export function buildLineAreaComputeWidgetData(
  response: Record<string, unknown> | null,
  opts: { widgetId: string; title?: string; capabilities?: Record<string, unknown>; format?: Record<string, unknown> },
): Record<string, unknown> {
  const rows =
    extractLineAreaComputeRows(response?.['responseList']) ??
    extractLineAreaComputeRows(response) ??
    extractLineAreaFlatRows(response?.['responseList']) ??
    extractLineAreaFlatRows(response) ??
    [];
  const caps = opts.capabilities ?? {};
  const firstRow = rows[0] ?? {};
  const rowKeys = Object.keys(firstRow);
  const configuredCategory = String(caps['category'] ?? '');
  const configuredCategoryLabel = String(caps['categoryLabel'] ?? '');
  const configuredMeasures = Array.isArray(caps['measures']) ? (caps['measures'] as Array<Record<string, unknown>>) : [];
  const resolvedMeasures = configuredMeasures
    .map((measure) => {
      const label = String(measure['label'] ?? '');
      const field = String(measure['field'] ?? '');
      const key = label && rowKeys.includes(label) ? label : field && rowKeys.includes(field) ? field : '';
      return key ? { ...measure, field: key, label: label || key } : null;
    })
    .filter((measure): measure is { field: string; label: string } & Record<string, unknown> => measure !== null);
  if (resolvedMeasures.length) {
    const measureKeys = new Set(resolvedMeasures.map((measure) => String(measure['field'])));
    const categoryKey =
      [configuredCategory, configuredCategoryLabel].find((key) => key && rowKeys.includes(key))
      || rowKeys.find((key) => !measureKeys.has(key) && typeof firstRow[key] !== 'number')
      || configuredCategory;
    // A series split / secondary X survives ONLY when its column actually rides
    // the response rows; the secondary-X value series follows the same
    // displayName remap as the measures.
    const seriesField = String(caps['series'] ?? '');
    const secondaryXField = String(caps['secondaryXField'] ?? '');
    const secondaryXValue = String(caps['secondaryXValueField'] ?? '');
    const secondaryXValueMeasure = configuredMeasures.find((measure) => String(measure['field'] ?? '') === secondaryXValue);
    const resolvedSecondaryXValue = rowKeys.includes(secondaryXValue)
      ? secondaryXValue
      : String(secondaryXValueMeasure?.['label'] ?? '') && rowKeys.includes(String(secondaryXValueMeasure?.['label'] ?? ''))
        ? String(secondaryXValueMeasure?.['label'])
        : '';
    const capabilities = {
      ...caps,
      category: categoryKey,
      categoryLabel: configuredCategoryLabel || configuredCategory || categoryKey,
      measures: resolvedMeasures,
      ...(configuredCategory && configuredCategory !== categoryKey
        ? { crossFilterKey: (caps['crossFilterKey'] as string | undefined) || configuredCategory }
        : {}),
      ...(seriesField && !rowKeys.includes(seriesField) ? { series: undefined } : {}),
      computedColumns: undefined,
      ...(secondaryXField && rowKeys.includes(secondaryXField) && resolvedSecondaryXValue
        ? { secondaryXValueField: resolvedSecondaryXValue }
        : { secondaryXField: undefined, secondaryXValueField: undefined, secondaryXPosition: undefined }),
    };
    return {
      widgetId: opts.widgetId,
      title: opts.title ?? '',
      capabilities,
      staticData: rows,
      height: '100%',
    };
  }
  const categoryKey =
    (configuredCategory && rowKeys.includes(configuredCategory) ? configuredCategory : '')
    || (rowKeys.includes('department') ? 'department' : '')
    || rowKeys.find((key) => key !== 'value' && typeof firstRow[key] !== 'number')
    || rowKeys.find((key) => key !== 'value')
    || configuredCategory
    || 'department';
  const valueKey =
    (rowKeys.includes('value') ? 'value' : '')
    || rowKeys.find((key) => key !== categoryKey && typeof firstRow[key] === 'number')
    || 'value';
  const firstMeasure = (Array.isArray(caps['measures']) ? (caps['measures'] as Array<Record<string, unknown>>)[0] : undefined);
  const capabilities = {
    ...caps,
    category: categoryKey,
    categoryLabel: String(caps['categoryLabel'] ?? '') || configuredCategory || categoryKey,
    measures: [{
      field: valueKey,
      label: String(firstMeasure?.['label'] ?? '') || 'Value',
      renderAs: (firstMeasure?.['renderAs'] as string) || 'line',
      axis: 'y',
    }],
    // Cross-filter publishes a column other tiles filter their RAW rows by, so
    // it keeps the real column, not the remapped `category`.
    ...(configuredCategory && configuredCategory !== categoryKey
      ? { crossFilterKey: (caps['crossFilterKey'] as string | undefined) || configuredCategory }
      : {}),
    // All meaningless on pre-aggregated rows: the single-series envelope can't
    // express a series split or a secondary X axis (its column isn't in the
    // rows), so carrying them through drew a ghost secondary axis + legend entry.
    series: undefined,
    computedColumns: undefined,
    secondaryXField: undefined,
    secondaryXValueField: undefined,
    secondaryXPosition: undefined,
  };
  return {
    widgetId: opts.widgetId,
    title: opts.title ?? '',
    capabilities,
    staticData: rows,
    height: '100%',
  };
}

/** Build the HorizontalBarChartComponent `data` input from a compute response. */
export function buildHbWidgetData(
  response: Record<string, unknown> | null,
  opts: { widgetId: string; title?: string; yAxisName?: string; xAxisName?: string; format?: Record<string, unknown> },
): Record<string, unknown> {
  const rows =
    extractHorizontalBarRows(response?.['responseList']) ??
    extractHorizontalBarRows(response) ??
    [];
  return applySharedFormatFields({
    staticData: rows,
    categoryKey: 'department',
    valueKey: 'value',
    widgetId: opts.widgetId,
    title: opts.title ?? '',
    height: '100%',
    fullYAxisLabels: true,
    yAxisLabelWidth: 150,
    valueLabelPosition: 'inside',
    yAxisName: opts.yAxisName ?? '',
    xAxisName: opts.xAxisName ?? 'Value',
    showXAxis: true,
    color: opts.format?.['positiveColor'],
    barColor: opts.format?.['positiveColor'],
    ...(opts.format?.['showValues'] === false ? { showValues: false, valueLabelPosition: 'none' } : {}),
  }, opts.format);
}

/** Pull the KPI's single numeric value out of the compute response. */
function extractKpiValue(response: Record<string, unknown> | null, kpiField = ''): number | string | null {
  if (response == null) return null;
  const list = (response as { responseList?: Array<Record<string, unknown>> }).responseList;
  const fromList = Array.isArray(list) ? (list[0]?.['data'] as unknown) : undefined;
  const dataField = fromList ?? (response as { data?: unknown }).data ?? response;
  const row = Array.isArray(dataField) ? dataField[0] : dataField;
  if (row == null) return null;
  if (typeof row === 'number' || typeof row === 'string') return row;
  const record = row as Record<string, unknown>;
  const candidate =
    record['value'] ?? record['result'] ?? (kpiField ? record[kpiField] : undefined) ??
    Object.values(record).find((v) => typeof v === 'number');
  return (candidate as number | string | undefined) ?? null;
}

/** Build the NumberStatsComponent `data` input from a compute response. */
export function buildKpiWidgetData(
  response: Record<string, unknown> | null,
  opts: { widgetId: string; title?: string; kpiField?: string; format?: Record<string, unknown> },
): Record<string, unknown> {
  const value = extractKpiValue(response, opts.kpiField);
  return applySharedFormatFields({
    title: opts.title || 'KPI',
    key: 'value',
    widgetId: opts.widgetId,
    // NO explicit alignment — NumberStats falls back to the theme's
    // `statCard.align`. Hardcoding 'center' here meant a KPI that had been
    // RECOMPUTED (input/slider/dropdown change) jumped to centred while a
    // freshly built one stayed on the theme alignment: the "some centred, some
    // left" mismatch. Alignment now comes from one place, the theme.
    data: { data: [{ value }] },
  }, opts.format);
}

/** Resolve a recompute entry's chart type. The compute API is inconsistent —
 *  a WATERFALL/HBAR/KPI result carries `chartType`, but the drill-down result
 *  comes back with `chartType: null` and only `componentType: "TABLE"`. So fall
 *  back to componentType, then to the componentId, to classify it. Returns one
 *  of the RecomputeChartType strings, or '' if it can't be classified. */
export function resolveRecomputeChartType(entry: Record<string, unknown>): string {
  const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
  const chartType = String(result?.['chartType'] ?? '').toUpperCase();
  if (chartType) return chartType;
  const componentType = String(result?.['componentType'] ?? '').toUpperCase();
  if (componentType === 'TABLE') return 'DRILL_DOWN_TABLE';
  // A target that is itself an input control (slider/input box) comes back as
  // `componentType: "INPUT"` with `chartType: null` — same shape as a KPI's
  // single-value result, just a different target kind.
  if (componentType === 'INPUT') return 'INPUT';
  const id = String(result?.['componentId'] ?? entry?.['componentId'] ?? '').toUpperCase();
  if (id.includes('DRILL')) return 'DRILL_DOWN_TABLE';
  if (id.includes('HBAR') || id.includes('HORIZONTAL')) return 'HORIZONTAL_BAR_CHART';
  if (id.includes('WATERFALL')) return 'WATERFALL';
  if (id.includes('KPI')) return 'KPI';
  if (id.includes('TMATRIX')) return 'TABLE_MATRIX';
  return '';
}

/** True when a recompute `result` entry actually carries chart data. The
 *  compute API can return placeholder entries for every target chart while only
 *  SOME are recomputed — applying an empty one blanks/loads that chart, so
 *  callers skip entries that fail this check and leave those charts untouched. */
export function recomputeEntryHasData(entry: Record<string, unknown>): boolean {
  const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
  const chartType = resolveRecomputeChartType(entry);
  const data = result?.['data'];
  switch (chartType) {
    case 'WATERFALL':
    case 'DRILL_DOWN_TABLE':
      return Array.isArray(data) && data.length > 0;
    // Tables usually reach here as componentType:"TABLE" (→ DRILL_DOWN_TABLE
    // above), but a backend that labels the entry chartType:"TABLE_MATRIX"
    // fell through to the default and was silently dropped.
    case 'TABLE_MATRIX': {
      const rows = Array.isArray(data) ? data : (data as { data?: unknown } | undefined)?.data;
      return Array.isArray(rows) && rows.length > 0;
    }
    case 'HORIZONTAL_BAR_CHART': {
      const rows = extractHorizontalBarRows((result as { responseList?: unknown }).responseList) ?? extractHorizontalBarRows(result);
      return Array.isArray(rows) && rows.length > 0;
    }
    case 'BAR': {
      const rows = extractBarComputeRows((result as { responseList?: unknown }).responseList) ?? extractBarComputeRows(result);
      return Array.isArray(rows) && rows.length > 0;
    }
    // Client-side rendered families: a recompute entry is only usable when it
    // actually carries replacement rows (the builder swaps them into the tile's
    // staticData). Without these cases every LINE_AREA/SCATTER_BUBBLE entry was
    // silently dropped by the default branch, so those charts never reacted to
    // input-driven recomputes at all.
    case 'LINE_AREA': {
      // Server-computed envelope (`data[0].lineArea`) OR flat replacement rows.
      const envelope = extractLineAreaComputeRows((result as { responseList?: unknown }).responseList) ?? extractLineAreaComputeRows(result);
      if (envelope) return envelope.length > 0;
      const rows = Array.isArray(data) ? data : (data as { data?: unknown } | undefined)?.data;
      return Array.isArray(rows) && rows.length > 0;
    }
    case 'SCATTER_BUBBLE': {
      const rows = Array.isArray(data) ? data : (data as { data?: unknown } | undefined)?.data;
      return Array.isArray(rows) && rows.length > 0;
    }
    case 'KPI':
    case 'INPUT': {
      const row = Array.isArray(data) ? data[0] : data;
      return row != null;
    }
    default:
      return false;
  }
}

/** Build the `data`-shaped fields for a target chart from a recompute `result`
 *  entry (`{ componentId, label, result: { chartType, data, … } }`). Returns the
 *  fields to merge onto the existing widget config, or null for unknown types.
 *  Identity fields (type/layout/height/componentId/payload) must be preserved by
 *  the caller — only the data-bearing fields are returned here. */
export function buildRecomputedWidgetFields(
  entry: Record<string, unknown>,
  ctx: { widgetId: string; title?: string; yAxisName?: string; xAxisName?: string; kpiField?: string; format?: Record<string, unknown>; payload?: Record<string, unknown>; capabilities?: Record<string, unknown> },
): Record<string, unknown> | null {
  const result = (entry?.['result'] ?? entry) as Record<string, unknown>;
  const chartType = resolveRecomputeChartType(entry);
  switch (chartType) {
    case 'WATERFALL': {
      const rawBars = (Array.isArray(result['data'])
        ? result['data']
        : ((result['data'] as { data?: WaterfallBarLike[] })?.data || [])) as WaterfallBarLike[];
      const bars = applyWaterfallStepSigns(rawBars, ctx.payload);
      return { option: buildWaterfallOption(bars, ctx.format), waterfallBars: bars };
    }
    case 'DRILL_DOWN_TABLE': {
      // Editable levels/measures come from the widget's compute payload (the
      // builder writes `editable: true` on each flagged dimension/measure) or,
      // for a table-matrix tile, its capabilities mirror of the same lists.
      const drillCaps = ctx.capabilities ?? (ctx.payload?.['capabilities'] as Record<string, unknown> | undefined);
      return buildDrillDownWidgetData(result, {
        widgetId: ctx.widgetId,
        fallbackHeaderName: ctx.title,
        format: ctx.format,
        dimensions: (ctx.payload?.['dimensions'] ?? drillCaps?.['dimensions']) as DrillDownEditConfig['dimensions'],
        measures: (ctx.payload?.['measures'] ?? drillCaps?.['measures']) as DrillDownEditConfig['measures'],
      });
    }
    case 'HORIZONTAL_BAR_CHART':
      return buildHbWidgetData(result, { widgetId: ctx.widgetId, title: ctx.title, yAxisName: ctx.yAxisName, xAxisName: ctx.xAxisName, format: ctx.format });
    case 'BAR': {
      // Formula-driven bars, OR a bar already rendered from SERVER rows (its
      // capabilities.category is the remapped row key, e.g. 'department', so
      // the envelope rows fit it). An ordinary client-side bar (capabilities
      // keep the real dataset columns) is left alone — this pre-aggregated
      // response would flatten it, and the dropdown filters it locally instead.
      const barRows = extractBarComputeRows((result as { responseList?: unknown }).responseList) ?? extractBarComputeRows(result);
      const barCaps = ctx.capabilities ?? (ctx.payload?.['capabilities'] as Record<string, unknown> | undefined);
      const capCategory = String(barCaps?.['category'] ?? '');
      const serverRendered = !!barRows?.length && !!capCategory && capCategory in ((barRows[0] ?? {}) as Record<string, unknown>);
      if (!barPayloadHasValueFormula(ctx.payload) && !serverRendered) return null;
      return buildBarComputeWidgetData(result, {
        widgetId: ctx.widgetId,
        title: ctx.title,
        capabilities: barCaps,
        payload: ctx.payload,
        format: ctx.format,
      });
    }
    case 'LINE_AREA': {
      // Server-computed line-area — either the legacy `data[0].lineArea`
      // envelope or the multi-measure FLAT contract (rows keyed by the
      // columns' displayNames). Flat rows are only applied when they actually
      // fit the widget's configured category/measures, so a client-side-
      // rendered line chart fed unrelated rows is left alone.
      const laCaps = ctx.capabilities ?? (ctx.payload?.['capabilities'] as Record<string, unknown> | undefined);
      const laEnvelope = extractLineAreaComputeRows((result as { responseList?: unknown }).responseList) ?? extractLineAreaComputeRows(result);
      const laFlat = laEnvelope ? null : extractLineAreaFlatRows((result as { responseList?: unknown }).responseList) ?? extractLineAreaFlatRows(result);
      const laRows = laFlat?.length ? laFlat : laEnvelope;
      if (!laRows?.length || !lineAreaRowsFitCapabilities(laRows, laCaps)) return null;
      return buildLineAreaComputeWidgetData(result, {
        widgetId: ctx.widgetId,
        title: ctx.title,
        capabilities: laCaps,
        format: ctx.format,
      });
    }
    case 'SCATTER_BUBBLE': {
      // Scatter/bubble widgets render directly from their flat source rows.
      // Replace both copies kept by a published DVM widget: `staticData` is the
      // live input consumed by UnifiedScatterBubbleChartComponent, while
      // `data.data` is the persisted fallback used when the dashboard is
      // normalized/saved again after a recompute.
      const rows = Array.isArray(result['data'])
        ? result['data']
        : (result['data'] as { data?: unknown } | undefined)?.data;
      if (!Array.isArray(rows) || !rows.length) return null;
      return {
        staticData: rows,
        data: { data: rows },
      };
    }
    case 'KPI':
      return buildKpiWidgetData(result, { widgetId: ctx.widgetId, title: ctx.title, kpiField: ctx.kpiField, format: ctx.format });
    case 'INPUT':
      // A target that is itself an input control just gets its live `value`
      // updated (the backend already computed it from the driving control's
      // formula) — the caller is responsible for pushing this into whatever
      // field the actual slider/input-box widget renders from.
      return { value: extractKpiValue(result) };
    default:
      return null;
  }
}


